import { logger } from '@adornis/base/logging.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { loadRawEntityDataByIDOrFail } from '@adornis/bh-util/util.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { ComponentParamaterType } from '@adornis/whatsapp/server/types.js';
import { getCertificateByID, renderCertificateTemplate } from '../api/certificate.js';
import { getMailTemplateForInstitution } from '../api/mail-templates.js';
import { bhBaseQlContext } from '../bh-context.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { ParticipationWorkshop } from '../entities/ParticipationWorkshop.js';
import { Product } from '../entities/products.js';
import { getMergedProductSettings } from '../entities/settings.js';
import { TextMultipleLangItem } from '../entities/TextMultipleLangItem.js';
import { User } from '../entities/user.js';
import { assembleInstitutionMail } from '../mails/assemble-mail.js';
import { buildNewsletterOptinLink } from '../mails/mail-helpers.js';
import { institutionMailHint, institutionMailVariables } from '../mails/mail-template-catalog.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { getNotificationType, sendWhatsappWithEmailFallback } from './_helper.js';

export async function notifyNuzterTeilnahme(participationID: string, withCertificate: boolean) {
  const participation = await loadRawEntityDataByIDOrFail(ParticipationWorkshop, participationID, {
    _id: 1,
    _class: 1,
    userID: 1,
    productID: 1,
  });

  const user = await loadRawEntityDataByIDOrFail(User, participation.userID, {
    _id: 1,
    _class: 1,
    title: 1,
    firstName: 1,
    lastName: 1,
    dateOfBirth: 1,
    email: 1,
    whatsappNotification: 1,
    whatsappPhone: 1,
  });

  const product = await loadRawEntityDataByIDOrFail(Product, participation.productID, {
    _id: 1,
    _class: 1,
    name: 1,
    description: 1,
    sortingWeight: 1,
    hasPrice: 1,
    categoryID: 1,
    institutionID: 1,
    timeframeID: 1,
    publishDate: 1,
    topic: 1,
    language: 1,
    presenceType: 1,
    isCanceled: 1,
    appointments: { _class: 1, _id: 1, start: 1, end: 1, isNonBlocking: 1, location: 1, name: 1 },
    capacity: 1,
    hideCapacity: 1,
    attachedCertificate: {
      certificateID: 1,
      descriptionsHTML: selectionSet(() => TextMultipleLangItem, 2),
      exportDate: 1,
    },
    translatedDescriptionHTML: selectionSet(() => TextMultipleLangItem, 2),
  });

  const productSettings = await getMergedProductSettings(participation.productID, [
    'evaluationLink',
    'evaluationLinkCode',
    'colorDefinitions',
  ]);

  const institutionID = product.institutionID;

  // Set mail theme
  bhBaseQlContext.colorDefinitions = productSettings.colorDefinitions;

  const mailTemplateType = withCertificate
    ? MailTemplateType.NUTZER_TEILNAHME
    : MailTemplateType.NUTZER_TEILNAHME_OHNE_ZERTIFIKAT;
  const mailTemplate = await getMailTemplateForInstitution(
    institutionID,
    mailTemplateType,
  )(InstitutionMailTemplate.allFields);

  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  const renderCertificatePdf = async () => {
    const certificateID = product.attachedCertificate?.certificateID;
    if (!certificateID) throw new Error('Cannot generate certificate for product without linked certificate');
    const certificate = await getCertificateByID(certificateID)({
      _id: 1,
      fileID: 1,
      file: { _id: 1, meta: { extension: 1 } },
    });
    if (!certificate?.file) throw new Error(`No certificate template file found for ID ${certificateID}`);
    return renderCertificateTemplate(
      certificate.file,
      constructValue<Product>(product),
      constructValue<Parameters<typeof renderCertificateTemplate>[2]>(user),
    );
  };

  const sendEmail = async () => {
    const mailVars = {
      [MailTemplateVars.TITLE]: user.title ?? '',
      [MailTemplateVars.FIRST_NAME]: user.firstName,
      [MailTemplateVars.LAST_NAME]: user.lastName,
      [MailTemplateVars.EVENT_TITLE]: product.name,
      [MailTemplateVars.PRODUCT_NAME]: product.name,
      [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
      [MailTemplateVars.EVALUATION_LINK]: productSettings.evaluationLink ?? '',
      [MailTemplateVars.EVALUATION_CODE]: productSettings.evaluationLinkCode ?? '',
      [MailTemplateVars.NEWSLETTER_OPTIN_LINK]: buildNewsletterOptinLink(token, 'de'),
      [MailTemplateVars.NEWSLETTER_OPTIN_LINK_EN]: buildNewsletterOptinLink(token, 'en'),
    };

    const { subject, content } = await assembleInstitutionMail({ template: mailTemplate, infos: mailVars as any });

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    const fullEmailRec = { email: user.email, institutionID, content, subject } as Parameters<
      typeof sendInstitutionEmail
    >[0];

    if (withCertificate) {
      const buffer = await renderCertificatePdf();
      fullEmailRec.attachments = [{ filename: 'Teilnahmezertifikat.pdf', content: buffer }];
    }

    return await sendInstitutionEmail(fullEmailRec);
  };

  const notificationType = await getNotificationType(user, mailTemplate, institutionID);
  if (notificationType === 'whatsapp') {
    let certificateServeLink: string | undefined;
    if (withCertificate) {
      const { AdornisFile } = await import('@adornis/file-utils/db/files.js');
      const { mongo } = await import('../server/mongo.js');
      const { createSignedFileServeUrl } = await import('../server/signed-file-url.js');
      const buffer = await renderCertificatePdf();
      const file = await AdornisFile.createFromBuffer(buffer, 'Teilnahmezertifikat.pdf');
      // ownerID is intentionally undeclared on AdornisFile.
      await mongo()
        .collection<{ _id: string }>(AdornisFile._collectionName)
        .updateOne({ _id: file._id }, { $set: { ownerID: user._id } });
      certificateServeLink = createSignedFileServeUrl(file._id, 'Teilnahmezertifikat.pdf', 86400);
    }
    const result = await sendWhatsappWithEmailFallback({
      institutionID,
      user: { _id: user._id, whatsappPhone: user.whatsappPhone!, email: user.email },
      mailTemplate,
      mailTemplateType,
      parameters: [
        InstitutionMailTemplate.mapFirstNameVar(user) || '-',
        InstitutionMailTemplate.mapLastNameVar(user) || '-',
        product.name || '-',
      ],
      headerParameters: withCertificate
        ? [{ type: ComponentParamaterType.DOCUMENT, document: { link: certificateServeLink! } }]
        : undefined,
      sendEmail,
    });
    return result.ok;
  } else if (notificationType === 'email') {
    return await sendEmail();
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
    return false;
  }
}

// --- Mail Template Definition ---
defineMailTemplate(MailTemplateType.NUTZER_TEILNAHME, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Nutzer-Teilnahme mit Zertifikat',
    content: `<p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>vielen Dank f&uuml;r den Besuch der Veranstaltung "{{event-title}}". Wir schicken Ihnen beigef&uuml;gt Ihren Teilnahmenachweis zu.</p>
<p>Wir freuen uns, wenn wir Sie in Zukunft in einer unserer Veranstaltungen begr&uuml;&szlig;en d&uuml;rfen und empfehlen Ihnen hierf&uuml;r unser Vortrags- und Workshop-Programm auf <a href="https://anmeldung.uni-frankfurt.de">anmeldung.uni-frankfurt.de.</a></p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>thank you for attending the event&nbsp; "{{event-title}}". We are enclosing your attendance certificate.</p>
<p>We look forward to welcoming you to one of our events in the future and recommand our lecture and workshop programme at <a href="https://anmeldung.uni-frankfurt.de">anmeldung.uni-frankfurt.de</a> for this purpose</p>
<p>Best regards</p>`,
    footer: '[footer]',
  },
  variables: institutionMailVariables(MailTemplateType.NUTZER_TEILNAHME),
  attachments: [],
  displayName: 'Teilnahmebestätigung',
  editorHint: () => institutionMailHint(MailTemplateType.NUTZER_TEILNAHME),
});

defineMailTemplate(MailTemplateType.NUTZER_TEILNAHME_OHNE_ZERTIFIKAT, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Nutzer-Teilnahme ohne Zertifikat',
    content: `<p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>vielen Dank f&uuml;r den Besuch der Veranstaltung "{{event-title}}".</p>
<p>Wir freuen uns, wenn wir Sie in Zukunft in einer unserer Veranstaltungen begr&uuml;&szlig;en d&uuml;rfen und empfehlen Ihnen hierf&uuml;r unser Vortrags- und Workshop-Programm auf <a href="https://anmeldung.uni-frankfurt.de">anmeldung.uni-frankfurt.de.</a></p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>thank you for attending the event&nbsp; "{{event-title}}".</p>
<p>We look forward to welcoming you to one of our events in the future and recommand our lecture and workshop programme at <a href="https://anmeldung.uni-frankfurt.de">anmeldung.uni-frankfurt.de</a> for this purpose</p>
<p>Best regards</p>`,
    footer: '[footer]',
  },
  variables: institutionMailVariables(MailTemplateType.NUTZER_TEILNAHME),
  attachments: [],
  displayName: 'Teilnahmebestätigung ohne Zertifikat',
  editorHint: () => institutionMailHint(MailTemplateType.NUTZER_TEILNAHME_OHNE_ZERTIFIKAT),
});
